import React, { useEffect, useRef } from 'react';
import { ShieldCheck, ShieldAlert } from 'lucide-react';
import { BrandRiskAnalysis } from '../../../shared/types';
import { BrandRiskStatus } from '@/hooks/useBrandRiskQueue';
import { Pill } from '@/components/flat';

interface BrandRiskPillProps {
  domain: string;
  isAvailable: boolean;
  status: BrandRiskStatus;
  result?: BrandRiskAnalysis;
  onVisible: (domain: string) => void;
  onViewRisk: () => void;
  onRetry: (domain: string) => void;
}

const BrandRiskPill: React.FC<BrandRiskPillProps> = ({
  domain, isAvailable, status, result, onVisible, onViewRisk, onRetry,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasMarkedRef = useRef(false);
  const onVisibleRef = useRef(onVisible);
  useEffect(() => { onVisibleRef.current = onVisible; });

  useEffect(() => {
    if (!isAvailable || hasMarkedRef.current) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasMarkedRef.current) {
          hasMarkedRef.current = true;
          onVisibleRef.current(domain);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [domain, isAvailable]);

  const content = (() => {
    if (!isAvailable) {
      return <Pill variant="muted">Domain taken</Pill>;
    }

    if (status === 'idle' || status === 'analyzing') {
      return (
        <span
          className="inline-block h-5 w-20 bg-gray-200 rounded-md animate-pulse"
          aria-label="Checking brand risk…"
        />
      );
    }

    if (status === 'done' && result) {
      const variant =
        result.riskLevel === 'low' ? 'success' :
        result.riskLevel === 'medium' ? 'warning' : 'error';
      const label =
        result.riskLevel === 'low' ? 'Clear' :
        result.riskLevel === 'medium' ? 'Caution' : 'Risk';
      const Icon = result.riskLevel === 'low' ? ShieldCheck : ShieldAlert;

      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onViewRisk(); }}
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-md"
          aria-label={`Brand risk: ${label}. Click to view details.`}
        >
          <Pill variant={variant}>
            <Icon className="h-3 w-3" />
            {label}
          </Pill>
        </button>
      );
    }

    if (status === 'error') {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRetry(domain); }}
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-md"
          aria-label="Brand risk check failed. Click to retry."
        >
          <Pill variant="muted" className="cursor-pointer hover:bg-gray-200 transition-colors">
            Couldn't check · retry
          </Pill>
        </button>
      );
    }

    return null;
  })();

  return <div ref={containerRef}>{content}</div>;
};

export default BrandRiskPill;
